<template>
  <div class="relative">
    <SalesPopularityTag :is-out-of-stock :popularity-stats />
    <RevCard :id="item.listingId" data-qa="cart">
      <p
        v-if="isOutOfStock"
        class="text-static-danger-hi body-1-bold p-16 pb-0 md:p-24 md:pb-0"
      >
        {{ i18n(translations.outOfStock) }}
      </p>

      <Product
        :is-out-of-stock
        :item
        :replacements
        :replacements-status
        @updated="emit('updated')"
        @updating="emit('updating')"
      />

      <Replacement
        v-if="isOutOfStock && original && replacements && replacements.length"
        :category-id="item.marketplaceCategoryId"
        :item
        :mobile-plan="item.mobilePlan"
        :original
        :replacements
        @replaced="emit('updated')"
        @replacing="emit('updating')"
      />

      <ErrorBoundary :owners="['bot-squad-b2cservices-front']">
        <SelectedMobilePlan
          v-if="item.mobilePlan && !isOutOfStock"
          class="p-16 md:p-24"
          :mobile-plan-offer="item.mobilePlan.selectedOffer"
          @remove="handleMobilePlanRemoval"
        />

        <Insurances
          v-if="!isOutOfStock && item.insuranceOffers.length > 0"
          :insurance-offers="item.insuranceOffers"
          :listing-id="item.listingId"
          :model="item.model"
          :with-catch-up-modal="isFirstItemWithUnselectedInsurance"
          @next="emit('next')"
          @update="handleInsuranceUpdate"
        />
      </ErrorBoundary>
    </RevCard>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { AsyncDataRequestStatus } from '#app/composables/asyncData'
import type { InsuranceOfferWithSelectedState } from '@backmarket/http-api'
import type { CartItem } from '@backmarket/http-api/src/api-specs-checkout/cart/cart.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import ErrorBoundary from '@backmarket/nuxt-module-logger/components/ErrorBoundary.vue'
import { RevCard } from '@ds/components/Card'

import type { ReplacementOption } from '~/api/checkout/cart/types'
import type { PopularityStats } from '~/api/checkout/popularity-stats/types'

import translations from './CartItemCard.translations'
import Insurances from './components/Insurances/Insurances.vue'
import Product from './components/Product/Product.vue'
import Replacement from './components/Replacement/Replacement.vue'
import SalesPopularityTag from './components/SalesPopularityTag/SalesPopularityTag.vue'
import SelectedMobilePlan from './components/SelectedMobilePlan/SelectedMobilePlan.vue'

const props = defineProps<{
  item: CartItem
  popularityStats?: PopularityStats
  original?: ReplacementOption
  replacements?: Array<ReplacementOption>
  replacementsStatus?: AsyncDataRequestStatus
  isFirstItemWithUnselectedInsurance: boolean
}>()

const emit = defineEmits<{
  updating: [updatedItem?: CartItem]
  updated: []
  next: []
}>()

const i18n = useI18n()

function handleInsuranceUpdate(
  updatedInsuranceOffers: InsuranceOfferWithSelectedState[],
) {
  const updatedItem = {
    ...props.item,
    insuranceOffers: updatedInsuranceOffers,
  }

  emit('updating', updatedItem)
  emit('updated')
}

function handleMobilePlanRemoval() {
  const updatedItem = {
    ...props.item,
    mobilePlan: null,
  }

  emit('updating', updatedItem)
  emit('updated')
}

const isOutOfStock = computed(() => props.item.available !== 'yes')
</script>
